import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Code2,
  Download,
  Loader2,
  Pencil,
  RefreshCcw,
  ScrollText,
  ShieldCheck,
  Trash2,
  Wand2
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import { getChatStatus, sendChatMessage } from '../lib/api/chat'
import { AGENT_ROLE_LABELS, AGENT_ROLES, getSystemPrompts, SystemPrompt } from '../lib/api/systemPrompts'
import {
  createModelCost,
  deleteModelCost,
  getModelCosts,
  ModelCost,
  ModelCostPayload,
  updateModelCost
} from '../lib/api/modelCosts'
import { downloadCsv } from '../utils/csv'

const defaultTestMessage = 'Give me a concise hiking safety tip for a beginner trail.'

const PROMPT_CSV_COLUMNS = [
  'Name',
  'Description',
  'PromptContent',
  'Model',
  'Role',
  'IsActive',
  'ModelName',
  'ModelInputCostPerMillion',
  'ModelOutputCostPerMillion'
]

const normalizeModelKey = (value: string) => value.trim().toLowerCase()

const formatModelNameFromKey = (key: string): string => {
  const modelPart = key.trim().split('/').filter(Boolean).at(-1) ?? key.trim()
  const words = modelPart
    .split(/[-_:]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      if (/^gpt$/i.test(part)) return 'GPT'
      if (/^oss$/i.test(part)) return 'OSS'
      if (/^[a-z]{1,3}$/i.test(part)) return part.toUpperCase()
      if (/^\d/.test(part)) return part.toUpperCase()
      return part.charAt(0).toUpperCase() + part.slice(1)
    })

  return words.join(' ') || key.trim()
}

export const SystemPromptsPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [prompts, setPrompts] = useState<SystemPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [chatConfigured, setChatConfigured] = useState<boolean | null>(null)
  const [testInput, setTestInput] = useState(defaultTestMessage)
  const [testResults, setTestResults] = useState<Record<string, string>>({})
  const [models, setModels] = useState<ModelCost[]>([])
  const [modelForm, setModelForm] = useState<ModelCostPayload>({
    name: '',
    key: '',
    inputCostPerMillion: 0,
    outputCostPerMillion: 0
  })
  const [modelEditingId, setModelEditingId] = useState<string | null>(null)
  const [modelSaving, setModelSaving] = useState(false)
  const [modelLoading, setModelLoading] = useState(false)
  const [isCsvExporting, setIsCsvExporting] = useState(false)

  const role = (user?.role ?? 'Guest').toString()
  const isEditor = role === 'Admin' || role === 'Editor'

  const hydrate = useCallback(async () => {
    setLoading(true)
    try {
      const [list, status, modelList] = await Promise.all([getSystemPrompts(), getChatStatus(), getModelCosts()])
      setPrompts(list)
      setChatConfigured(status.isConfigured)
      setModels(modelList)
    } catch (error) {
      console.error('Failed to load system prompts', error)
      addToast({
        title: 'Could not load system prompts',
        description: 'Please refresh or try again later.'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isEditor) {
      setLoading(false)
      return
    }
    void hydrate()
  }, [hydrate, isEditor])

  const sortedPrompts = useMemo(() => {
    const order = new Map(AGENT_ROLES.map((role, index) => [role as string, index]))
    return [...prompts].sort(
      (a, b) => (order.get(a.role ?? '') ?? AGENT_ROLES.length) - (order.get(b.role ?? '') ?? AGENT_ROLES.length)
    )
  }, [prompts])

  const handleTest = async (prompt: SystemPrompt) => {
    if (!chatConfigured) {
      addToast({
        title: 'Chat is not configured',
        description: 'Set OPENROUTER_API_KEY to enable testing.'
      })
      return
    }

    setTestingId(prompt.id)
    try {
      const response = await sendChatMessage(
        [{ role: 'user', content: testInput || defaultTestMessage }],
        { systemPromptId: prompt.id, model: prompt.model }
      )
      setTestResults((prev) => ({
        ...prev,
        [prompt.id]: response.message.content
      }))
      addToast({ title: 'Test complete', description: 'Response received from the model.' })
    } catch (error) {
      console.error('Failed to test prompt', error)
      const axiosErr = error as { response?: { data?: { error?: string } } }
      const message = axiosErr?.response?.data?.error ?? 'Test request failed.'
      setTestResults((prev) => ({
        ...prev,
        [prompt.id]: `Error: ${message}`
      }))
      addToast({ title: 'Test failed', description: message })
    } finally {
      setTestingId(null)
    }
  }

  const handleExportCsv = () => {
    setIsCsvExporting(true)
    try {
      const modelsByKey = new Map(models.map((model) => [normalizeModelKey(model.key), model]))
      const rows = prompts.map((p) => [
        p.name,
        p.description ?? '',
        p.promptContent,
        p.model,
        p.role ?? '',
        p.isActive.toString(),
        modelsByKey.get(normalizeModelKey(p.model))?.name ?? formatModelNameFromKey(p.model),
        (modelsByKey.get(normalizeModelKey(p.model))?.inputCostPerMillion ?? '').toString(),
        (modelsByKey.get(normalizeModelKey(p.model))?.outputCostPerMillion ?? '').toString()
      ])
      const timestamp = new Date().toISOString().slice(0, 10)
      downloadCsv([...PROMPT_CSV_COLUMNS], rows, `hikestrong-system-prompts-${timestamp}.csv`)
      addToast({ title: 'CSV exported', description: `Exported ${rows.length} prompts.` })
    } catch (error) {
      console.error('Failed to export prompts CSV', error)
      addToast({ title: 'Export failed', description: 'Could not export the CSV file.' })
    } finally {
      setIsCsvExporting(false)
    }
  }

  if (!isEditor) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Limited Access
          </CardTitle>
          <CardDescription>
            System prompts can only be viewed by Admin or Editor accounts. Contact an admin if you need access.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">AI Coaching</p>
          <h1 className="text-3xl font-semibold">System Prompts</h1>
          <p className="text-sm text-muted-foreground">
            Prompts and models are defined in backend code so every environment and eval runs the same configuration.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={hydrate} title="Reload system prompts">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <Card className="bg-slate-900 text-slate-50 shadow-xl">
            <CardHeader className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-emerald-500/20 p-3 ring-1 ring-emerald-500/30">
                  <Code2 className="h-6 w-6 text-emerald-200" />
                </div>
                <div>
                  <CardTitle className="text-lg">Defined in code</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {prompts.length > 0 ? `${prompts.length} agent prompt(s) baked in.` : 'No prompts loaded.'}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-200/80 font-semibold">
                {chatConfigured ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Chat configured
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-amber-200" />
                    Chat not configured
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              To change a prompt or model, edit{' '}
              <code className="text-emerald-200">backend/Services/SystemPromptCatalog.cs</code> and deploy. This keeps
              local, staging, production, and evals consistent.
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Model library</CardTitle>
              <CardDescription>Manage OpenRouter models and token costs (used for cost reporting).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium text-foreground" htmlFor="model-name">
                      Name
                    </label>
                    <Input
                      id="model-name"
                      value={modelForm.name}
                      onChange={(e) => setModelForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., GPT-5 Nano"
                      disabled={modelSaving}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground" htmlFor="model-key">
                      Key
                    </label>
                    <Input
                      id="model-key"
                      value={modelForm.key}
                      onChange={(e) => setModelForm((prev) => ({ ...prev, key: e.target.value }))}
                      placeholder="openai/gpt-5-nano"
                      disabled={modelSaving}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium text-foreground" htmlFor="model-input">
                      Input $/1M tokens
                    </label>
                    <Input
                      id="model-input"
                      type="number"
                      min={0}
                      step="0.01"
                      value={modelForm.inputCostPerMillion}
                      onChange={(e) =>
                        setModelForm((prev) => ({
                          ...prev,
                          inputCostPerMillion: Number.parseFloat(e.target.value || '0')
                        }))
                      }
                      disabled={modelSaving}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground" htmlFor="model-output">
                      Output $/1M tokens
                    </label>
                    <Input
                      id="model-output"
                      type="number"
                      min={0}
                      step="0.01"
                      value={modelForm.outputCostPerMillion}
                      onChange={(e) =>
                        setModelForm((prev) => ({
                          ...prev,
                          outputCostPerMillion: Number.parseFloat(e.target.value || '0')
                        }))
                      }
                      disabled={modelSaving}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={async () => {
                      setModelSaving(true)
                      try {
                        if (modelEditingId) {
                          const updated = await updateModelCost(modelEditingId, modelForm)
                          setModels((prev) => prev.map((m) => (m.id === updated.id ? updated : m)))
                          addToast({ title: 'Model updated', description: updated.name })
                        } else {
                          const created = await createModelCost(modelForm)
                          setModels((prev) => [...prev, created])
                          addToast({ title: 'Model added', description: created.name })
                        }
                        setModelForm({ name: '', key: '', inputCostPerMillion: 0, outputCostPerMillion: 0 })
                        setModelEditingId(null)
                      } catch (error) {
                        const axiosErr = error as { response?: { data?: { error?: string } } }
                        const message = axiosErr?.response?.data?.error ?? 'Could not save model.'
                        addToast({ title: 'Save failed', description: message, variant: 'error' })
                      } finally {
                        setModelSaving(false)
                      }
                    }}
                    disabled={modelSaving}
                    className="gap-2"
                  >
                    {modelSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {modelEditingId ? 'Update model' : 'Add model'}
                  </Button>
                  {modelEditingId ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setModelForm({ name: '', key: '', inputCostPerMillion: 0, outputCostPerMillion: 0 })
                        setModelEditingId(null)
                      }}
                      disabled={modelSaving}
                    >
                      Cancel
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Saved models</p>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={async () => {
                      setModelLoading(true)
                      try {
                        const data = await getModelCosts()
                        setModels(data)
                        addToast({ title: 'Models refreshed' })
                      } catch {
                        addToast({ title: 'Refresh failed', description: 'Could not reload models.' })
                      } finally {
                        setModelLoading(false)
                      }
                    }}
                    disabled={modelLoading}
                    title="Refresh models"
                  >
                    {modelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                  </Button>
                </div>
                {modelLoading ? (
                  <p className="text-sm text-muted-foreground">Loading models…</p>
                ) : models.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No models yet.</p>
                ) : (
                  <div className="space-y-2">
                    {models.map((model) => (
                      <div
                        key={model.id}
                        className="flex items-start justify-between rounded-md border border-muted/60 bg-muted/20 px-3 py-2 text-sm"
                      >
                        <div className="space-y-1">
                          <p className="font-semibold text-foreground">{model.name}</p>
                          <p className="text-xs text-muted-foreground">{model.key}</p>
                          <p className="text-xs text-muted-foreground">
                            In: ${model.inputCostPerMillion.toFixed(2)} /M · Out: $
                            {model.outputCostPerMillion.toFixed(2)} /M
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => {
                              setModelEditingId(model.id)
                              setModelForm({
                                name: model.name,
                                key: model.key,
                                inputCostPerMillion: model.inputCostPerMillion,
                                outputCostPerMillion: model.outputCostPerMillion
                              })
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={async () => {
                              const confirmed = window.confirm(`Delete model "${model.name}"?`)
                              if (!confirmed) return
                              try {
                                await deleteModelCost(model.id)
                                setModels((prev) => prev.filter((m) => m.id !== model.id))
                                addToast({ title: 'Model deleted', description: model.name })
                              } catch {
                                addToast({ title: 'Delete failed', description: 'Could not delete this model.' })
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-primary" />
                Quick test message
              </CardTitle>
              <CardDescription>Used when you click “Test” on any prompt.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                value={testInput}
                onChange={(event) => setTestInput(event.target.value)}
                rows={3}
                placeholder={defaultTestMessage}
                disabled={!chatConfigured}
              />
              {!chatConfigured ? (
                <p className="text-xs text-amber-600">
                  Chat is not configured. Set OPENROUTER_API_KEY to run tests.
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Agent prompts</h2>
              <p className="text-sm text-muted-foreground">
                One prompt per agent role. Read-only — edit in backend code.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={isCsvExporting || loading || prompts.length === 0}
              className="gap-2"
            >
              {isCsvExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </Button>
          </div>

          {loading ? (
            <Card className="flex items-center gap-3 px-6 py-5">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading prompts…</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {sortedPrompts.map((prompt) => (
                <Card key={prompt.id} className="shadow-sm">
                  <CardHeader className="space-y-2">
                    <div className="space-y-1">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <ScrollText className="h-5 w-5 text-primary" />
                        {prompt.name}
                        {prompt.role ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">
                            {AGENT_ROLE_LABELS[prompt.role]}
                          </span>
                        ) : null}
                      </CardTitle>
                      <CardDescription className="text-sm text-foreground">
                        {prompt.description || 'No description provided.'}
                      </CardDescription>
                      <p className="text-xs text-muted-foreground">
                        Model: <span className="font-medium text-foreground">{prompt.model}</span>
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="rounded-md border bg-muted/40 p-3 text-sm leading-relaxed">
                      <pre className="whitespace-pre-wrap font-sans text-foreground">{prompt.promptContent}</pre>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(prompt)}
                        disabled={testingId === prompt.id || !chatConfigured}
                        className="gap-2"
                      >
                        {testingId === prompt.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Wand2 className="h-4 w-4" />
                        )}
                        Test
                      </Button>
                      <div className="flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-800">
                        <ShieldCheck className="h-3 w-3" />
                        Live for {prompt.role ? AGENT_ROLE_LABELS[prompt.role] : 'unassigned role'}
                      </div>
                    </div>
                    {testResults[prompt.id] ? (
                      <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                        <p className="mb-1 text-xs font-semibold uppercase text-primary">Test response</p>
                        <p className="whitespace-pre-wrap text-foreground">{testResults[prompt.id]}</p>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
